import React from "react";
import { Badge, UncontrolledTooltip } from "reactstrap";
import CustomSelect from "components/customs/dropdown";
import { ACCOUNT_HOUSE_SPEAKER } from "utility/constants";
import { statusSelectStyles } from "constants/style";
import { ALLOCATION_STATUS_OPTIONS, ALLOCATION_STATUS } from "constants/status";
import { getFormattedDateString } from "utility/helpers";
import { Eye } from "react-feather";
import styled from "styled-components";
import { AGENCY_DPWH_ID } from "constants/agency";
import { cannotEditStatus } from "views/representatives/congressman/utility";
import { VIEW_ACTION } from "constants/actions";
import { getStatusOptions } from "./utility";

const StyledCustomSelect = styled(CustomSelect)`
  width: 100%;

  .select__control:has(.pending-status) {
    background-color: #00316d;
    border: none;
    .pending-status {
      color: white;
    }
  }

  .select__control:has(.requested-status) {
    background-color: #f3c61c;
    border: none;
    .requested-status {
      color: black;
    }
  }

  .select__control:has(.processed-status) {
    background-color: #265ea3;
    border: none;
    .processed-status {
      color: white;
    }
  }

  .select__control:has(.in-progress-status) {
    background-color: #ffbd14;
    border: none;
    .in-progress-status {
      color: black;
    }
  }

  .select__control:has(.approved-status) {
    background-color: #53a24c;
    border: none;
    .approved-status {
      color: white;
    }
  }

  .select__control:has(.disapproved-status) {
    background-color: #df3348;
    border: none;
    .disapproved-status {
      color: white;
    }
  }

  .select__control:has(.downloaded-status) {
    background-color: #f26e01;
    border: none;
    .downloaded-status {
      color: white;
    }
  }

  .select__control:has(.resubmission-status) {
    background-color: #ff4433;
    border: none;
    .resubmission-status {
      color: white;
    }
  }
`;

export const columnTable = ({
  role,
  /* toggleModal,
  setEditingCongressman,
  fetchRecipientMsgs, */
  navigate,
}) => {
  const isPendingOrRequested = (status) =>
    status === ALLOCATION_STATUS.PENDING ||
    status === ALLOCATION_STATUS.REQUESTED;

  const columns = [
    {
      name: "",
      selector: null,
      sortable: false,
      id: "highlight",
      width: "5px",
      cell: ({ status }) => (
        <div
          className={`${
            status === ALLOCATION_STATUS.PENDING ||
            status === ALLOCATION_STATUS.REQUESTED
              ? "is-pending"
              : ""
          }`}
        />
      ),
    },
    {
      name: "",
      selector: (row) => row?.badge,
      sortable: false,
      width: "20px",
      id: "badge",
      cell: ({ status }, index) => {
        if (
          (status === ALLOCATION_STATUS.PENDING ||
            status === ALLOCATION_STATUS.REQUESTED) &&
          role !== ACCOUNT_HOUSE_SPEAKER
        ) {
          return (
            <Badge
              className={`badge ${
                // eslint-disable-next-line no-nested-ternary
                status === ALLOCATION_STATUS.PENDING
                  ? "is-pending"
                  : status === ALLOCATION_STATUS.REQUESTED
                  ? "is-requested"
                  : ""
              }`}
              pill
            >
              <div>{index}</div>
            </Badge>
          );
        }

        return null;
      },
    },
    {
      name: "ID",
      selector: (row) => row?.id,
      identifier: "id",
      sortable: true,
      minWidth: "max-content",
      center: true,
      style: { paddingLeft: 0, paddingRight: 0 },
      cell: (row) => (
        <p
          className={`mb-0 ${
            row.status === ALLOCATION_STATUS.PENDING ||
            row.status === ALLOCATION_STATUS.REQUESTED
              ? "font-weight-bold is-pending"
              : ""
          }`}
        >
          {row.id}
        </p>
      ),
    },
    {
      name: "Program",
      selector: (row) => row?.project,
      identifier: "project",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: (row) => (
        <p
          className={`mb-0 ${
            row.status === ALLOCATION_STATUS.PENDING ||
            row.status === ALLOCATION_STATUS.REQUESTED
              ? "font-weight-bold is-pending"
              : ""
          }`}
        >
          {row?.institution?.name}
        </p>
      ),
    },
    {
      name: "Recipient",
      selector: (row) => row?.recipient,
      identifier: "recipient",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: (row) => <p className="mb-0">{row.congressman}</p>,
    },
    {
      name: "Recommended Amount",
      selector: (row) => row?.budget_allocation,
      identifier: "budget_allocation",
      grow: 2.1,
      sortable: true,
      minWidth: "150px",
      cell: (row) => (
        <p
          className={`mb-0 ${
            row.status === ALLOCATION_STATUS.PENDING ||
            row.status === ALLOCATION_STATUS.REQUESTED
              ? "font-weight-bold is-pending"
              : ""
          }`}
        >
          {`PHP ${Number(row.budget_allocation)?.toLocaleString()}`}
        </p>
      ),
    },
    {
      name: "Date Requested",
      selector: (row) => row?.dateRequested,
      identifier: "dateRequested",
      sortable: true,
      minWidth: "150px",
      cell: ({ status, dateRequested }) => (
        <p
          className={`mb-0 ${
            status === ALLOCATION_STATUS.PENDING ||
            status === ALLOCATION_STATUS.REQUESTED
              ? "font-weight-bold is-pending"
              : ""
          }`}
        >
          {getFormattedDateString(dateRequested, "MMM-dd-yyyy")}
        </p>
      ),
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      identifier: "status",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: ({ status }) => (
        <StyledCustomSelect
          className={`${
            isPendingOrRequested(status) ? "font-weight-bold" : ""
          } ${cannotEditStatus(status, role) ? "" : "pointer"}`}
          isSearchable={false}
          isDisabled
          value={ALLOCATION_STATUS_OPTIONS.find(
            (stat) => stat.value === status
          )}
          options={getStatusOptions(status, role)}
          styles={statusSelectStyles}
          getOptionLabel={(option) => {
            return option?.displayLabel;
          }}
          menuPortalTarget={document.body}
        />
      ),
    },
    /* To follow: Comments section */
    /*  
    {
      name: "Remarks",
      selector: (row) => row?.hasComments,
      sortable: false,
      minWidth: "160px",
      cell: (row) => (
        <div
          className={`comment-section p-0 ${
            row.status === ALLOCATION_STATUS.PENDING ||
            row.status === ALLOCATION_STATUS.REQUESTED
              ? "is-pending"
              : ""
          }`}
        >
          {row.hasComments === 0 && hasPermission("comment-view") && (
            <AddCommentBtn
              size="sm"
              onClick={() => {
                setEditingCongressman(row.id);
                fetchRecipientMsgs(row.id);
                toggleModal();
              }}
            >
              {"Add Comment"}
            </AddCommentBtn>
          )}

          {row.hasComments === 1 && hasPermission("comment-create") && (
            <>
              {row.status === ALLOCATION_STATUS.DISAPPROVED &&
                row.unreadMsg !== 0 && (
                  <Badge
                    className="unread-msgs"
                    pill
                  >
                    {row.unreadMsg === 0 ? null : row.unreadMsg}
                  </Badge>
                )}

              <ViewCommentBtn
                size="sm"
                onClick={() => {
                  setEditingCongressman(row.id);
                  fetchRecipientMsgs(row.id);
                  toggleModal();
                }}
              >
                {"View Comment"}
              </ViewCommentBtn>
            </>
          )}
        </div>
      ),
    },
    */
    {
      name: "Actions",
      selector: (row) => row?.id,
      sortable: false,
      width: "150px",
      cell: (row, index) => {
        return (
          <>
            <Eye
              size={16}
              color="#00316d"
              className="mr-05"
              style={{ cursor: "pointer" }}
              onClick={() => {
                // View action - navigate to view page if needed
                // Adjust route based on your routing structure
                if (navigate) {
                  navigate(
                    `/projects/${AGENCY_DPWH_ID}/program/${row?.id}/recipient`,
                    {
                      state: {
                        row,
                        action: VIEW_ACTION,
                        agency: AGENCY_DPWH_ID,
                        list: "dpwh",
                      },
                    }
                  );
                }
              }}
              id={`view-row--${index}`}
            />
            <UncontrolledTooltip
              placement="top"
              target={`view-row--${index}`}
            >
              {"View"}
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];

  return columns;
};
